/**
 * Free Product Validator
 * Gère la validation et le retrait automatique des produits gratuits
 * lorsque les conditions d'éligibilité ne sont plus remplies
 */

if (!customElements.get('free-product-validator')) {
  customElements.define(
    'free-product-validator',
    class FreeProductValidator extends HTMLElement {
      constructor() {
        super();
        this._handleCartUpdate = this._handleCartUpdate.bind(this);
        this._isValidating = false;
        this._checkInterval = null;
        this._initialized = false;
      }

      connectedCallback() {
        this._init();
        
        // Gérer le retour via bfcache
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            this._init();
          }
        });
      }

      disconnectedCallback() {
        document.removeEventListener('deployer-cart-drawer:updated', this._handleCartUpdate);
        if (this._checkInterval) {
          clearInterval(this._checkInterval);
          this._checkInterval = null;
        }
      }

      _init() {
        if (this._initialized) return;
        this._initialized = true;
        
        // Écouter les mises à jour du panier
        document.addEventListener('deployer-cart-drawer:updated', this._handleCartUpdate);
        
        // Validation initiale au chargement
        this._validateCart();
      }

      _handleCartUpdate() {
        // Délai pour laisser le DOM se mettre à jour
        setTimeout(() => {
          this._validateCart();
        }, 300);
      }

      async _validateCart() {
        if (this._isValidating) return;
        this._isValidating = true;

        try {
          // Récupérer les données du panier
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cart = await cartResponse.json();
          
          // Récupérer le seuil depuis l'attribut data ou le cart drawer
          const threshold = this._getThreshold();
          
          if (!threshold) {
            this._isValidating = false;
            return;
          }

          // Identifier les produits gratuits dans le panier
          const freeProductItems = [];
          let totalWithoutFreeProducts = 0;

          for (const item of cart.items) {
            // Vérifier si c'est un produit gratuit via les propriétés ou le prix
            const isFreeProduct = this._isFreeProduct(item);
            
            if (isFreeProduct) {
              freeProductItems.push(item);
            } else {
              totalWithoutFreeProducts += item.final_line_price;
            }
          }

          // Si le panier contient des produits gratuits mais le seuil n'est pas atteint
          if (freeProductItems.length > 0 && totalWithoutFreeProducts < threshold) {
            await this._removeFreeProducts(freeProductItems);
          }
        } catch (error) {
          console.error('Erreur lors de la validation des produits gratuits:', error);
        } finally {
          this._isValidating = false;
        }
      }

      _getThreshold() {
        // Chercher le seuil dans le data attribute du composant
        const thresholdAttr = this.dataset.threshold;
        if (thresholdAttr) {
          return parseInt(thresholdAttr, 10);
        }

        // Fallback: chercher dans le cart-free-product component
        const cartFreeProduct = document.querySelector('cart-free-product[data-cart-value]');
        if (cartFreeProduct) {
          return parseInt(cartFreeProduct.dataset.cartValue, 10);
        }

        return null;
      }

      _isFreeProduct(item) {
        // Méthode 1: Vérifier via la propriété _is_gift ajoutée lors de l'ajout
        if (item.properties && item.properties._is_gift === true) {
          return true;
        }

        // Méthode 2: Vérifier si le prix est à 0 et que le produit est dans la liste des produits gratuits
        if (item.final_price === 0 || item.price === 0) {
          // Vérifier si c'est dans la liste des variant IDs gratuits
          const freeVariantIds = this._getFreeVariantIds();
          if (freeVariantIds.includes(item.variant_id)) {
            return true;
          }
        }

        // Méthode 3: Vérifier via l'attribut data-free-product sur l'élément du panier
        const cartItem = document.querySelector(`[data-free-product="true"][data-variant-id="${item.variant_id}"]`);
        if (cartItem) {
          return true;
        }

        return false;
      }

      _getFreeVariantIds() {
        // Récupérer les IDs des variantes des produits gratuits depuis les data attributes
        const freeVariantIdsAttr = this.dataset.freeVariantIds;
        if (freeVariantIdsAttr) {
          try {
            return JSON.parse(freeVariantIdsAttr);
          } catch {
            return [];
          }
        }
        return [];
      }

      async _removeFreeProducts(freeProductItems) {
        const updates = {};
        
        for (const item of freeProductItems) {
          updates[item.key] = 0;
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates })
          });

          if (response.ok) {
            // Déclencher le rafraîchissement du panier
            document.dispatchEvent(new CustomEvent('deployer-cart-drawer:refresh'));
            
            // Optionnel: afficher un message toast
            this._showToast('Le cadeau a été retiré car les conditions ne sont plus remplies.');
          }
        } catch (error) {
          console.error('Erreur lors du retrait des produits gratuits:', error);
        }
      }

      _showToast(message) {
        if (document.querySelector('deployer-toast-notification')) {
          document.dispatchEvent(new CustomEvent('toast:open', { 
            detail: { 
              type: 'info', 
              message: message 
            } 
          }));
        }
      }
    }
  );
}
